import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { WidgetTableColumn, WidgetTableColumnsData } from 'jamespot-user-api';

import { BETA_JRCDragAndDrop } from 'components/Beta/BETA_JRCDragAndDrop/BETA_JRCDragAndDrop';

import { CSSEditorComponentWrapper, CSSEditorComponentDescription } from 'components/Editors/components/Editor.style';

import { WidgetDatasourceTableContent } from 'jamespot-user-api';
import { Typography } from 'components/JRCTypography/JRCTypography.styles';

import { Utils } from 'index';
import { WidgetDatasourceModelViewColumn } from './WidgetDatasourceModelViewColumn';
import { WidgetDatasourceModelViewColumnAction } from './WidgetDatasourceModelViewColumnAction';

import { SUPPORTED_DISPLAY_TYPE } from '../const';
import { ModalColumnLabelize } from './ModalColumnLabelize';

import { CSSListItem } from '../JRCWidgetDatasourceTableEditor.styles';

import { WidgetDatasourceModelViewChanges } from './WidgetDatasourceModelViewChanges';

export const WidgetDatasourceModelView = ({
    model,
    excelHeaders,
    tableColumnsData,
    onChange,
    onUpdateModel,
}: {
    model: WidgetTableColumn[];
    excelHeaders?: string[] | undefined;
    tableColumnsData: WidgetTableColumnsData;
    onChange: (content: Partial<WidgetDatasourceTableContent>, override?: boolean) => void;
    onUpdateModel?: (model: WidgetTableColumn[]) => void;
}) => {
    const intl = useIntl();
    const [localdata, setlocaldata] = useState<WidgetTableColumnsData>(tableColumnsData);

    const [isOpenLabelEdit, setIsOpenLabelEdit] = useState<boolean>(false);
    const [name, setName] = useState<string | undefined>(undefined);

    const moveData = useCallback((dragIndex: number, hoverIndex: number) => {
        setlocaldata((prevData) => Utils.array.arraySwapElements(prevData, dragIndex, hoverIndex));
    }, []);

    useEffect(() => {
        setlocaldata(tableColumnsData);
    }, [tableColumnsData]);

    const columnDataEdit = useMemo(() => tableColumnsData.find((el) => el.name === name), [tableColumnsData, name]);

    return (
        <>
            <CSSEditorComponentWrapper>
                <Typography size={'l'}>{intl.formatMessage({ id: 'WIDGET_Datasource_Columns' })}</Typography>
                <CSSEditorComponentDescription>
                    {intl.formatMessage({
                        id: 'WIDGET_Datasource_Columns_Description',
                    })}
                </CSSEditorComponentDescription>
            </CSSEditorComponentWrapper>
            <CSSEditorComponentWrapper>
                <BETA_JRCDragAndDrop
                    onDrop={() => {
                        onChange({
                            tableColumnsData: localdata,
                        });
                    }}
                    onDrag={moveData}
                    elms={localdata}
                    renderEl={(item) => {
                        const column = model.find((el) => el.name === item.name);
                        const isActive = SUPPORTED_DISPLAY_TYPE.includes(column ? column.type : 'text');
                        const columnData = tableColumnsData.find((el) => el.name === item.name);
                        return column ? (
                            <CSSListItem isActive={isActive}>
                                <WidgetDatasourceModelViewColumn
                                    key={item.name}
                                    label={column.label}
                                    isVisible={columnData && columnData.isVisible}
                                    isActive={isActive}
                                    onChange={(isVisible) => {
                                        onChange({
                                            tableColumnsData: tableColumnsData.map((el) =>
                                                el.name === item.name ? { ...el, isVisible } : el,
                                            ),
                                        });
                                    }}
                                />
                                <WidgetDatasourceModelViewColumnAction
                                    tableColumnsData={tableColumnsData}
                                    item={item}
                                    type={column.type}
                                    handleEditLabel={() => {
                                        setName(item.name);
                                        setIsOpenLabelEdit(true);
                                    }}
                                    onChange={onChange}
                                />
                            </CSSListItem>
                        ) : (
                            <></>
                        );
                    }}
                />
                <WidgetDatasourceModelViewChanges
                    model={model}
                    excelHeaders={excelHeaders}
                    onChange={onUpdateModel ? onUpdateModel : () => undefined}
                />
                {isOpenLabelEdit && (
                    <ModalColumnLabelize
                        title={intl.formatMessage({ id: 'WIDGET_Table_Edit_Label' })}
                        initialValue={(columnDataEdit && columnDataEdit?.label) ?? undefined}
                        onChange={(label) => {
                            if (name === undefined) return;
                            onChange({
                                tableColumnsData: tableColumnsData.map((el) =>
                                    el.name === name ? { ...el, label } : el,
                                ),
                            });
                            setName(undefined);
                        }}
                        onCloseHandler={() => setIsOpenLabelEdit(false)}
                    />
                )}
            </CSSEditorComponentWrapper>
        </>
    );
};
